import { Box } from '@mui/material';
import { type ComponentProps, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ModalityChip from '~/components/Chip/ModalityChip';
import MenuChoice from '~/components/Menu/MenuChoice';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { distinguishingSeriesNumber } from './tileViewModel';

/** What a console menu says about an image, whichever list it appears in. */
export type ImageMenuRowProps = Omit<
    ComponentProps<typeof MenuChoice>,
    'chip' | 'name' | 'meta'
> & {
    /** Raw modality code, e.g. 'CT'. Not PHI. */
    readonly modality: string;
    /** Series description, or the localised "Unknown Series". PHI: masked here. */
    readonly label: string;
    /** DICOM SeriesNumber. Not PHI. */
    readonly seriesNumber?: number;
    /**
     * Whether another series of the list has the same modality and name, so
     * that the series number is what tells the two rows apart.
     */
    readonly sharesName?: boolean;
    /**
     * Names what makes this row different from another naming the same
     * image, e.g. the registration that places it. Shown as given: the
     * caller masks any patient data in it.
     */
    readonly qualifier?: ReactNode;
};

/**
 * One image as a choice of a menu: its modality as a chip, then its name,
 * on the same columns as every other menu choice. The name is masked with
 * the privacy toggle wherever the row is used, so no list can leak a
 * description the rest of the console hides. The series number follows
 * while names are masked, and where another series of the list has the
 * same modality and name, so no two rows read alike.
 *
 * The other choice props pass through, so the row can sit directly in a
 * Menu, which steers focus through its direct children's props.
 */
export default function ImageMenuRow({
    modality,
    label,
    seriesNumber,
    sharesName = false,
    qualifier,
    ...choiceProps
}: ImageMenuRowProps) {
    const intl = useIntl();
    const namesShown = useRecoilValue(showSensitiveInfoState);
    const seriesNumberLabel = distinguishingSeriesNumber(
        intl,
        seriesNumber,
        namesShown,
        sharesName
    );
    const hasMeta = qualifier !== undefined || seriesNumberLabel !== undefined;
    return (
        <MenuChoice
            {...choiceProps}
            chip={<ModalityChip modality={modality} size="small" />}
            name={<SensitiveInfoTypography>{label}</SensitiveInfoTypography>}
            meta={
                hasMeta ? (
                    <Box component="span" display="inline-flex" gap={1}>
                        {qualifier !== undefined && <span>{qualifier}</span>}
                        {seriesNumberLabel !== undefined && (
                            <span>{seriesNumberLabel}</span>
                        )}
                    </Box>
                ) : undefined
            }
        />
    );
}
